import { useState, useEffect } from "react";
import { Plus, Trash2, BookMarked, Search, Link, X, Check } from "lucide-react";
import { useAuth } from "../../../app/AuthContext";
import { useSchoolProfile } from "../../../app/SchoolProfileContext";
import { updateClass, type ClassData } from "../../../lib/api";

interface SubjectsManagerProps {
  classes: ClassData[];
  onRefresh?: () => void;
}

export default function SubjectsManager({
  classes,
  onRefresh,
}: SubjectsManagerProps) {
  const { token } = useAuth();
  const { activeProfile } = useSchoolProfile();
  const [customSubjects, setCustomSubjects] = useState<string[]>([]);
  const [newSubject, setNewSubject] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isAssociationModalOpen, setIsAssociationModalOpen] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [loading, setLoading] = useState<string | null>(null);

  useEffect(() => {
    if (!activeProfile) return;
    const key = `custom_subjects_${activeProfile.id}`;
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        setCustomSubjects(JSON.parse(saved));
      } catch (e) {
        console.error("Erro ao carregar matérias personalizadas:", e);
      }
    } else {
      setCustomSubjects([]);
    }
  }, [activeProfile]);

  const saveCustomSubjects = (subjects: string[]) => {
    setCustomSubjects(subjects);
    if (activeProfile) {
      const key = `custom_subjects_${activeProfile.id}`;
      localStorage.setItem(key, JSON.stringify(subjects));
    }
  };

  const handleAddSubject = () => {
    if (!newSubject.trim()) return;
    const trimmed = newSubject.trim();
    if (!customSubjects.includes(trimmed)) {
      saveCustomSubjects([...customSubjects, trimmed]);
    }
    setNewSubject("");
  };

  const handleRemoveSubject = (subject: string) => {
    if (
      window.confirm(`Tem certeza que deseja excluir a matéria "${subject}"?`)
    ) {
      saveCustomSubjects(customSubjects.filter((s) => s !== subject));
    }
  };

  const handleOpenAssociationModal = (subject: string) => {
    setSelectedSubject(subject);
    setIsAssociationModalOpen(true);
  };

  const [localClasses, setLocalClasses] = useState<ClassData[]>(classes);

  useEffect(() => {
    setLocalClasses(classes);
  }, [classes]);

  const handleToggleClass = async (
    classId: string,
    currentSubject: string | undefined,
  ) => {
    if (!token || !selectedSubject) return;

    const targetClass = localClasses.find((c) => c.id === classId);
    if (!targetClass) return;

    const newSubjectValue =
      currentSubject === selectedSubject ? "" : selectedSubject;

    // Optimistic update
    setLocalClasses((prev) =>
      prev.map((c) =>
        c.id === classId ? { ...c, subject: newSubjectValue } : c,
      ),
    );

    setLoading(classId);
    try {
      // Prune read-only fields that might cause PUT errors
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, created_at, updated_at, studentsCount, ...writableData } =
        targetClass as any;

      await updateClass(
        classId,
        {
          ...writableData,
          subject: newSubjectValue,
        },
        token,
      );

      if (onRefresh) {
        await onRefresh();
      }
    } catch (e) {
      console.error("Erro ao atualizar turma:", e);
      // Revert optimistic update on error
      setLocalClasses(classes);
      alert("Erro ao atualizar associação da turma.");
    } finally {
      setLoading(null);
    }
  };

  // Get subjects from classes
  const classSubjects = Array.from(
    new Set(localClasses.map((c) => c.subject).filter(Boolean)),
  ) as string[];

  // Merge and filter
  const allSubjects = Array.from(
    new Set([...classSubjects, ...customSubjects]),
  ).sort();
  const filteredSubjects = allSubjects.filter((s) =>
    s.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="bg-(--card-bg) rounded-xl shadow-sm border border-(--border-color)">
      <div className="p-6 border-b border-(--border-color)">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-(--text-primary)">
              Gerenciar Matérias
            </h3>
            <p className="text-sm text-(--text-tertiary)">
              Suas matérias são derivadas das turmas ou adicionadas manualmente
              aqui.
            </p>
          </div>
        </div>

        <div className="flex gap-4 mb-6">
          <div className="flex-1 relative">
            <Plus className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-(--text-tertiary)" />
            <input
              type="text"
              placeholder="Adicionar nova matéria..."
              value={newSubject}
              onChange={(e) => setNewSubject(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddSubject()}
              className="w-full pl-10 pr-4 py-2 border rounded-lg bg-(--input-bg) border-(--border-color) text-(--text-primary) outline-none focus:ring-2 focus:ring-(--primary-color)"
            />
          </div>
          <button
            onClick={handleAddSubject}
            disabled={!newSubject.trim()}
            className="px-6 py-2 bg-(--primary-color) text-white rounded-lg font-medium hover:opacity-90 transition disabled:opacity-50"
          >
            Adicionar
          </button>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-(--text-tertiary)" />
          <input
            type="text"
            placeholder="Buscar matérias..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg bg-(--input-bg) border-(--border-color) text-(--text-primary) outline-none focus:ring-1 focus:ring-(--border-color)"
          />
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSubjects.map((subject) => {
            const associatedClasses = localClasses.filter(
              (c) => c.subject === subject,
            );
            const classNames = associatedClasses.map((c) => c.name).join(", ");
            const isFromClass = associatedClasses.length > 0;

            return (
              <div
                key={subject}
                className="flex items-center justify-between p-4 rounded-xl border border-(--border-color) bg-(--bg-secondary) group hover:border-(--primary-color) transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-(--bg-accent) flex items-center justify-center">
                    <BookMarked className="w-4 h-4 text-(--primary-color)" />
                  </div>
                  <div className="min-w-0">
                    <span className="font-medium text-(--text-primary) block truncate">
                      {subject}
                    </span>
                    <p className="text-[10px] text-(--text-tertiary) uppercase tracking-wider font-bold truncate">
                      {isFromClass
                        ? `Turmas: ${classNames}`
                        : "Manual (Sem turma)"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleOpenAssociationModal(subject)}
                    className="p-2 text-(--text-tertiary) hover:text-(--primary-color) hover:bg-(--bg-accent) rounded-lg transition-all"
                    title="Associar Turmas"
                  >
                    <Link className="w-4 h-4" />
                  </button>
                  {customSubjects.includes(subject) && (
                    <button
                      onClick={() => handleRemoveSubject(subject)}
                      className="p-2 text-(--text-tertiary) hover:text-red-500 hover:bg-red-50 rounded-lg transition-all shrink-0"
                      title="Excluir Matéria"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {filteredSubjects.length === 0 && (
          <div className="text-center py-12">
            <BookMarked className="w-16 h-16 mx-auto mb-4 text-(--border-color)" />
            <h4 className="text-lg font-medium text-(--text-primary) mb-1">
              Nenhuma matéria encontrada
            </h4>
            <p className="text-sm text-(--text-tertiary)">
              Adicione sua primeira matéria acima para começar.
            </p>
          </div>
        )}
      </div>

      {isAssociationModalOpen && selectedSubject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-(--card-bg) rounded-xl shadow-xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200 border border-(--border-color)">
            <div
              className="px-6 py-4 border-b flex justify-between items-center bg-(--bg-secondary)"
              style={{ borderColor: "var(--border-color)" }}
            >
              <div>
                <h3 className="text-lg font-semibold text-(--text-primary)">
                  Associar Turmas
                </h3>
                <p className="text-xs text-(--text-tertiary)">
                  Selecione as turmas para a matéria:{" "}
                  <span className="font-bold text-(--primary-color)">
                    {selectedSubject}
                  </span>
                </p>
              </div>
              <button
                onClick={() => setIsAssociationModalOpen(false)}
                className="text-(--text-tertiary) hover:text-(--text-primary) p-2 hover:bg-(--bg-accent) rounded-lg transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 max-h-[60vh] overflow-y-auto custom-scrollbar">
              <div className="space-y-2">
                {localClasses.length === 0 ? (
                  <p className="text-center py-8 text-(--text-tertiary)">
                    Nenhuma turma cadastrada.
                  </p>
                ) : (
                  localClasses.map((cls) => {
                    const isAssociated = cls.subject === selectedSubject;
                    const isOtherSubject =
                      cls.subject && cls.subject !== selectedSubject;

                    return (
                      <div
                        key={cls.id}
                        className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
                          isAssociated
                            ? "border-(--primary-color) bg-(--bg-accent)"
                            : "border-(--border-color) hover:bg-(--bg-secondary)"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                            style={{
                              backgroundColor:
                                cls.color || "var(--primary-color)",
                            }}
                          >
                            <BookMarked className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-semibold text-(--text-primary)">
                              {cls.name}
                            </p>
                            {cls.subject && (
                              <p
                                className={`text-[10px] font-bold uppercase tracking-wider ${isAssociated ? "text-(--primary-color)" : "text-(--text-tertiary)"}`}
                              >
                                {isAssociated
                                  ? "Associada"
                                  : `Matéria: ${cls.subject}`}
                              </p>
                            )}
                          </div>
                        </div>

                        <button
                          onClick={() => handleToggleClass(cls.id, cls.subject)}
                          disabled={!!loading}
                          className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                            isAssociated
                              ? "bg-(--primary-color) text-white"
                              : "border-2 border-(--border-color) text-(--text-tertiary) hover:border-(--primary-color) hover:text-(--primary-color)"
                          }`}
                        >
                          {loading === cls.id ? (
                            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          ) : isAssociated ? (
                            <Check className="w-5 h-5" />
                          ) : (
                            <Plus className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="px-6 py-4 bg-(--bg-secondary) border-t border-(--border-color) flex justify-end">
              <button
                onClick={() => setIsAssociationModalOpen(false)}
                className="px-6 py-2 bg-(--primary-color) text-white rounded-lg font-medium hover:opacity-90 transition shadow-sm"
              >
                Concluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
